import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import SSTable from '../SSTable/SSTable';
import SSColumn from '../SSTable/SSColumn';
import ToolTipIcon from '../ToolTipIcon/ToolTipIcon';
import ToolTip from '../ToolTipIcon/ToolTip';
import AssetImage from '../Assets/AssetImage';
import { launchCreator } from '../../utils/launchCreator';
import { buildUrlForAddUniqueQueryParam } from '../../utils/updateQueryString';
import { checkPathAccess } from '../../utils/checkPathAccess';
import { getPermissions } from '../../utils/getPermissions';

export class AssignmentsTable extends Component {
  getName = item => (
    <div className="item-name poppins-regular  !text-[#00243E]">
      <ToolTip toolText={`Location ${item.parent}`}>
        <Link
          className="poppins-regular  !text-[#00243E] appearance-none"
          to={item.type === 'device' ? `/devices/${item.device_id}` : this.getUrl(item)}
        >
          {item.name}
        </Link>
      </ToolTip>
    </div>
  );

  getDescription = assignment => {
    const { description } = assignment;
    let { tags = [] } = assignment || {};

    if (!tags || !Array.isArray(tags)) {
      tags = [];
    }

    return (
      <div
        data-test="assignment-description-container"
        className="item-name poppins-regular !text-[#00243E] flex flex-col gap-1"
      >
        {description && <div className="poppins-regular !text-[#00243E]">{description}</div>}
        {tags.length > 0 && (
          <div data-test="assignment-tags" className="flex flex-wrap gap-1">
            {tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center rounded-md bg-orange-50 px-2 py-1 text-xs font-medium text-sunrise inset-ring inset-ring-orange-600/20"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  getIcon = assignment => {
    return (
      <ToolTip toolText={assignment.type}>
        <img
          data-test="assignment-icon"
          src={
            assignment.type === 'location'
              ? '/assets/images/icons/globe_no_bg.svg'
              : '/assets/images/icons/devices_no_bg.svg'
          }
          alt="assignment-icon"
          className="flex-shrink-0 max-w-none w-[24px] h-[24px]"
        />
      </ToolTip>
    );
  };

  getPopulated = assignment => {
    if (assignment.type !== 'location') {
      return null;
    }

    const assigned = this.getAssignedContentCount(assignment);
    const total = this.getChildDevices(assignment);

    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-sunrise/10 text-dark-night ring-sunrise/30">
        {`${assigned}/${total}`}
      </span>
    );
  };

  getAssignedContent = item => {
    const { auth, signs } = this.props;
    const p = getPermissions(auth);

    if (item.type !== 'device') {
      return null;
    }

    return (
      <div data-test="assigned-content" className="flex items-center gap-2 min-w-0">
        <div data-test="assigned-content-image" className="flex-shrink-0">
          <AssetImage id={item.content_id} type={item.content_type} item={item} signs={signs} />
        </div>
        <div className="min-w-0 flex flex-col gap-0.5">
          <div
            data-test="assigned-content-name"
            className="poppins-regular text-dark-night truncate"
          >
            {!p.atLeastTierPro && item.content_type === 'sign' ? (
              item.content_name
            ) : (
              <Link
                className="!text-dark-night hover:!text-sunrise !no-underline"
                to={`/${item.content_type}s/${item.content_id}`}
              >
                {item.content_name}
              </Link>
            )}
          </div>
          {item.content_type && (
            <span
              data-test="assigned-content-type"
              className="inline-flex w-fit items-center rounded-full bg-sunrise/10 px-2 py-0.5 text-xs font-medium text-dark-night ring-1 ring-inset ring-sunrise/30"
            >
              {item.content_type}
            </span>
          )}
          <div data-test="assigned-content-actions" className="flex items-center gap-1 mt-0.5">
            {['sign', 'sequence', 'schedule'].includes(item.content_type) && (
              <ToolTipIcon
                toolText={`Preview ${item.content_name}`}
                href={`/player/?${item.content_type}=${item.content_id}`}
                target="_blank"
              >
                <img
                  className="w-[22px] h-[22px] opacity-70 hover:opacity-100 transition-opacity"
                  src="/assets/images/icons/play_no_bg.svg"
                  alt="preview"
                />
              </ToolTipIcon>
            )}
            {p.atLeastTierPro && item.content_type === 'sign' && (
              <ToolTipIcon
                toolText={`Open ${item.content_name} in Creator`}
                onClick={() => launchCreator(item.content_id)}
              >
                <i className="fa fa-paint-brush text-dark-night text-sm opacity-70 hover:opacity-100 transition-opacity" />
              </ToolTipIcon>
            )}
          </div>
        </div>
      </div>
    );
  };

  getAssignedSource = item => {
    if (item.type !== 'device' || !item.content_default_stream_type) {
      return null;
    }

    return (
      <div data-test="assigned-source" className="flex items-center gap-2 min-w-0">
        <div data-test="assigned-source-image" className="flex-shrink-0">
          <AssetImage
            id={item.content_default_stream_id}
            type={item.content_default_stream_type}
            item={item}
          />
        </div>
        <div className="min-w-0 flex flex-col gap-0.5">
          <div
            data-test="assigned-source-name"
            className="poppins-regular text-dark-night truncate"
          >
            {item.content_default_stream_type !== 'playlist' && item.content_default_stream_name}
            {item.content_default_stream_type === 'playlist' && (
              <Link
                className="!text-dark-night hover:!text-sunrise !no-underline"
                to={`/playlists/${item.content_default_stream_id}`}
              >
                {item.content_default_stream_name}
              </Link>
            )}
          </div>
          {item.content_default_stream_type && (
            <span
              data-test="assigned-source-type"
              className="inline-flex w-fit items-center rounded-full bg-sunrise/10 px-2 py-0.5 text-xs font-medium text-dark-night ring-1 ring-inset ring-sunrise/30"
            >
              {item.content_default_stream_type}
            </span>
          )}
        </div>
      </div>
    );
  };

  getChildDevices = assignment => {
    const { allData } = this.props;
    const parentPath = `${assignment.parent + assignment.name}/`;
    let counter = 0;

    if (allData) {
      allData.forEach(item => {
        const isAChildDevice = item.type === 'device' && item.parent.startsWith(parentPath);

        if (isAChildDevice) {
          counter += 1;
        }
      });
    }

    return counter;
  };

  getAssignedContentCount = assignment => {
    const { allData } = this.props;
    const parentPath = `${assignment.parent + assignment.name}/`;
    let counter = 0;

    if (allData) {
      allData.forEach(item => {
        const hasContentAssigned =
          item.type === 'device' &&
          item.parent.startsWith(parentPath) &&
          item.content_type !== 'none';

        if (hasContentAssigned) {
          counter += 1;
        }
      });
    }

    return counter;
  };

  nextOrViewDevice = assignment => {
    const {
      campaign: { id },
    } = this.props;

    if (assignment.type === 'device') {
      return (
        <div className="assignment-icons">
          <ToolTipIcon
            toolText={`Preview campaign for device: ${assignment.name}`}
            href={`/player/?device=${assignment.device_id}&campaign=${id}`}
            target="_blank"
          >
            <img
              className="w-[30px] h-[30px]"
              src="/assets/images/icons/play_no_bg.svg"
              alt="Play"
            />
          </ToolTipIcon>
          <ToolTipIcon toolText="Device Overview" href={`/devices/${assignment.device_id}`}>
            <img
              className="w-[30px] h-[30px]"
              src="/assets/images/icons/device_overview_no_bg.svg"
              alt="View Device"
            />
          </ToolTipIcon>
        </div>
      );
    }

    return (
      <Link
        to={this.getUrl(assignment)}
        onClick={this.update}
        className="!rounded-[15px] w-[59px] px-4 p-2 hover:!text-white !appearance-none !no-underline !cursor-pointer !h-[26px] !bg-btn-primary-form !text-white poppins-bold !font-[500]"
      >
        View
      </Link>
    );
  };

  getUrl = item => {
    const { location } = this.props;
    const parent = `${item.parent + item.name}/`;

    return buildUrlForAddUniqueQueryParam(location, 'parent', parent);
  };

  onDoubleClick = (event, item) => {
    const { history } = this.props;

    if (item.type === 'location') {
      history.push(this.getUrl(item));
    }
  };

  isDisabled = () => {
    const { userLocation } = this.props;

    if (userLocation) {
      return item => {
        const { parent, name } = item;
        const location = `${parent}${name}/`;

        return !checkPathAccess(userLocation, location);
      };
    }

    return () => false;
  };

  getType = assignment => assignment.type;

  render() {
    const { data, selected, onClick } = this.props;

    return (
      <SSTable
        data={data}
        rowHeight={80}
        className="responsive table-assignments"
        rowsCount={data.length - 1}
        onSelect={onClick}
        doubleClick={this.onDoubleClick}
        selected={selected}
        zebra
        selectable
        isDisabled={this.isDisabled()}
      >
        <SSColumn width={1} cell={this.getIcon} header="Type" />
        <SSColumn width={3} cell={this.getName} header="Name" />
        <SSColumn width={4} cell={this.getDescription} header="Description" />
        <SSColumn width={2} center cell={this.getPopulated} header="Populated" />
        <SSColumn width={3} cell={this.getAssignedContent} header="Content" />
        <SSColumn width={3} cell={this.getAssignedSource} header="Video Source" />
        <SSColumn
          containerClasses="!w-[70px]"
          center
          width={2}
          cell={this.nextOrViewDevice}
          header=""
        />
      </SSTable>
    );
  }
}

AssignmentsTable.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
  signs: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  allData: PropTypes.array.isRequired,
  selected: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  userLocation: PropTypes.string.isRequired,
};

export default withRouter(AssignmentsTable);
